<template>
  <div>
    <Spinner v-if="!isInitialized" />
    <div class="news-container" v-if="isInitialized" :class="{ small: maxmimizedUI }">
      <div class="editor-container">
        <CoverImage v-if="maxmimizedUI" :image="image" @close="maxmimizedUI = false" />
        <Editor
          v-bind="content"
          :theme="pageTheme"
          :maxmimizedUI="maxmimizedUI"
          @editor:update="editorUpdate"
          @editor:resize="onResize"
        />
      </div>
      <div class="settings">
        <div class="top-row">
          <v-btn @click="onNewsPublish" text-color="white" :color="colors[news.news_publish]"
            >Nyheten är {{ news.news_publish === 'online' ? 'online' : 'ett utkast' }}</v-btn
          >
          <v-btn
            elevation="2"
            color="primary"
            class="mb-2"
            @click="save"
            :disabled="!hasUnsavedUpdates"
            :loading="isSaving"
            >Spara</v-btn
          >
          <CommonMenu
            :items="[
              { key: 'remove', value: 'Radera nyhet' },
              { key: 'markAsBubble', value: 'Visa som bubbla' },
            ]"
            @remove="onRemove"
            @markAsBubble="onMarkAsBubble"
          />
        </div>
        <div class="preview-link">
          <PreviewLink domain="havet" :string="news.news_title" />
        </div>
        <PlaceInBubble v-model="newsMeta" />
        <TemplateNewsStandard
          v-bind="news"
          @update="update"
          @update-immediately="saveImmediately"
          class="my-3"
          @news:create:link="onCreateLink"
          :loading="isSaving"
        />
        <!-- {{ page }} -->
        <h4>Taggar</h4>
        <v-chip-group v-model="newsTags" column multiple>
          <v-chip
            text-color=""
            color="primary"
            v-for="(tag, i) in allTags"
            :key="i"
            filter
            outlined
          >
            {{ tag.tag }}
          </v-chip>
        </v-chip-group>
        <hr />
      </div>
      <Message ref="message" />
      <Confirm ref="confirm" />
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line no-unused-vars
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Editor from '@/components/shared/editor/Editor'
import TemplateNewsStandard from '@/components/templates/NewsStandard'
import Message from '@/components/shared/Message'
import Spinner from '@/components/shared/Spinner'
import Confirm from '@/components/shared/Confirm'
import { colors } from '@/assets/data/colors'
import CoverImage from '@/components/shared/CoverImage'
import CommonMenu from '@/components/shared/CommonMenu'
import PreviewLink from '@/components/shared/PreviewLink'
import { defaultNewsMetaData } from '@/assets/data/metaData.js'
import PlaceInBubble from '@/components/shared/PlaceInBubble'

export default {
  name: 'SingleNews',
  props: {
    id: { type: [Number, String], required: true },
  },
  components: {
    Editor,
    TemplateNewsStandard,
    Message,
    Spinner,
    Confirm,
    CoverImage,
    CommonMenu,
    PreviewLink,
    PlaceInBubble,
  },
  data() {
    return {
      maxmimizedUI: false,
      colors,
      isInitialized: false,
      isSaving: false,
      updates: {},
      updatesContent: {},
    }
  },
  computed: {
    ...mapGetters('tags', { getAllTags: 'getArray' }),
    ...mapGetters('news', { getNewsById: 'getById' }),
    ...mapGetters('content', { getContent: 'getContentByParentId' }),

    hasUnsavedUpdates() {
      return !!Object.keys({ ...this.updates, ...this.updatesContent }).length
    },

    news() {
      return { ...this.getNewsById(+this.newsId), ...this.updates }
    },

    image() {
      return `${process.env.VUE_APP_IMAGE_BACKEND}/870/${this.news.news_image_id}.jpg`
    },

    content() {
      return this.getContent(this.newsId)
    },
    contentId() {
      return this.content.id
    },
    newsId() {
      return Number(this.id)
    },

    pageTheme() {
      return 'havet-theme'
    },

    newsTags: {
      get() {
        return this.news.tags
          ? this.news.tags.map((t) => this.allTags.findIndex((at) => at.id === t.tag.id))
          : []
      },
      set(selectedTagsIndices) {
        const tags = selectedTagsIndices.map((i) => this.allTags[i]).map((t) => ({ tag: t }))
        this.update({ tags })
      },
    },

    allTags() {
      const allTags = this.getAllTags
      return allTags.sort((a, b) => a.group - b.group).sort((a, b) => a.tag.localeCompare(b.tag))
    },
    newsMeta: {
      get() {
        return this.news.news_meta ? JSON.parse(this.news.news_meta) : defaultNewsMetaData
      },
      set(val) {
        this.saveImmediately({ news_meta: val })
      },
    },
  },
  methods: {
    ...mapActions('tags', { tagsReadCollection: 'readCollection' }),
    ...mapActions('news', { getSingleNews: 'read', updateNews: 'update', deleteNews: 'delete' }),
    ...mapActions('content', {
      getSingleContent: 'read',
      updateContent: 'update',
      deleteContent: 'delete',
    }),
    ...mapActions('links', { createLink: 'create' }),
    ...mapMutations('tags', { mutTagsFilter: 'ADD_FILTER' }),

    async onRemove() {
      const shouldRemove = await this.$refs.confirm.open({
        title: 'Ta bort nyhet',
        message: 'Är du säker på att du vill ta bort nyheten?',
        saveButtonText: 'Ja',
        cancelButtonText: 'Avbryt',
      })
      if (!shouldRemove) return

      await this.deleteNews({ ...this.getNewsById(+this.newsId) })
      this.$router.replace({ name: 'NewsList' })
    },

    onMarkAsBubble() {
      const payload = { news_meta: JSON.stringify({ ...this.newsMeta, bubble: true }) }
      this.saveImmediately(payload)
    },

    async onCreateLink(_link) {
      const link = await this.createLink(_link)
      const currentNewsSources = this.news.newsLinks || []
      const payload = [...currentNewsSources, { link, nl_news_id: +this.id }].map((link, i) => ({
        ...link,
        nl_order: i,
      }))
      this.saveImmediately({ newsLinks: payload })
    },

    update(payload) {
      this.updates = { ...this.updates, ...payload }
    },

    editorUpdate(payload) {
      this.updatesContent = payload
    },
    async saveImmediately(payload) {
      this.isSaving = true
      await this.updateNews({ ...this.news, ...payload })
      this.isSaving = false
    },
    async save() {
      try {
        if (!this.hasUnsavedUpdates) return
        this.isSaving = true
        await this.updateNews({ ...this.news, ...this.updates })
        if (Object.keys(this.updatesContent).length)
          await this.updateContent({ ...this.content, ...this.updatesContent })
        this.updates = {}
        this.updatesContent = {}
        this.$refs.message.open('Nyheten sparad')
      } catch (err) {
        throw new Error(err)
      } finally {
        this.isSaving = false
      }
    },
    async onNewsPublish() {
      const newState = this.news.news_publish === 'online' ? 'offline' : 'online'
      await this.saveImmediately({ news_publish: newState })
    },
    onResize(val) {
      this.maxmimizedUI = val
    },
  },

  async created() {
    this.mutTagsFilter({ key: 'pageSize', value: 9999 })
    await this.tagsReadCollection()
    await this.getSingleNews({ id: this.id })
    await this.getSingleContent({ type: 'news', id: this.id })
    this.isInitialized = true
  },
  mounted() {
    this._keyListener = function (e) {
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault() // present "Save Page" from getting triggered.
        this.save()
      }
    }
    document.addEventListener('keydown', this._keyListener.bind(this))
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._keyListener)
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedUpdates) {
      next()
      return
    }
    const shouldSave = await this.$refs.confirm.open({
      title: 'Osparade ändringar',
      message: 'Vill du spara innen du går vidare?',
      saveButtonText: 'Spara',
      cancelButtonText: 'Nej',
    })
    if (shouldSave) await this.save()

    next()
  },
}
</script>

<style lang="less" scoped>
.news-container {
  display: grid;
  grid-template-columns: 1fr 500px;
  gap: 15px;
  transition: all 0.3s;
  &.small {
    grid-template-columns: max(1100px);
    justify-content: center;
    & > div:nth-child(2) {
      visibility: hidden;
      display: none;
    }
  }
}
.settings {
  display: flex;
  flex-direction: column;
  gap: 5px;
  .top-row {
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    gap: 15px;
    flex-wrap: wrap;
  }
}
</style>
